import { Check, Copy } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/client/components/ui/button";
import { cn } from "@/client/lib/utils";

/**
 * Small icon button that copies `value` to the clipboard and briefly swaps to a
 * check to confirm. Kept generic so any copyable value (SSH keys, IPs, ...) can
 * reuse it (PE-951 / PE-939).
 */
export function CopyButton({
  value,
  label = "Copy",
  className,
}: {
  value: string;
  label?: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy to the clipboard.");
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn("text-muted-foreground size-7 shrink-0", className)}
      onClick={onCopy}
      aria-label={copied ? "Copied" : label}
      title={label}
    >
      {copied ? (
        <Check className="size-3.5 text-[var(--success-fg)]" />
      ) : (
        <Copy className="size-3.5" />
      )}
    </Button>
  );
}
